import type { CoordEntry, CoordItem } from "../../types";
import type { MixCoordinate } from "../mixes/types";
import { buildMixEntries } from "../mixes/selectors";
import type { FriendCode, FriendCodeListItem, FriendCoordinateReference } from "./types";

type ResolvedCoordinate = { name: string; items: CoordItem[]; entry: CoordEntry | null };

export function catalogCoordinateId(entry: CoordEntry): string {
  const itemIds = entry.items.map((item) => item.id.trim()).filter(Boolean).sort();
  return itemIds.length ? itemIds.join("+") : entry.key;
}

function resolveCoordinate(
  reference: FriendCoordinateReference,
  catalogEntriesById: ReadonlyMap<string, CoordEntry>,
  mixEntriesById: ReadonlyMap<string, CoordEntry>,
): ResolvedCoordinate {
  if (!reference) return { name: "コーデ未設定", items: [], entry: null };
  const entry = reference.type === "mixed"
    ? mixEntriesById.get(reference.id)
    : catalogEntriesById.get(reference.id);
  return entry
    ? { name: entry.name, items: entry.items, entry }
    : { name: "不明なコーデ", items: [], entry: null };
}

function timestamp(value: string): number {
  const result = Date.parse(value);
  return Number.isFinite(result) ? result : 0;
}

export function buildFriendCodeListItems(
  codes: readonly FriendCode[],
  catalogEntries: readonly CoordEntry[],
  mixes: readonly MixCoordinate[],
): FriendCodeListItem[] {
  const mixEntries = buildMixEntries(mixes, catalogEntries).entries;
  const catalogEntriesById = new Map(catalogEntries.map((entry) => [catalogCoordinateId(entry), entry]));
  const mixEntriesById = new Map(mixEntries.flatMap((entry) => entry.mix ? [[entry.mix.id, entry] as const] : []));
  return codes.map((code) => {
    const resolved = resolveCoordinate(code.coordinate, catalogEntriesById, mixEntriesById);
    return {
      key: code.id,
      playerId: code.playerId,
      playerName: code.playerName,
      coordinate: code.coordinate,
      coordinateName: resolved.name,
      entry: resolved.entry,
      items: resolved.items,
      code,
    };
  }).sort((left, right) => timestamp(right.code.createdAt) - timestamp(left.code.createdAt));
}

export function friendDisplayEntry(record: FriendCodeListItem): CoordEntry {
  return record.entry ? {
    ...record.entry,
    key: `friend:${record.key}`,
    name: `${record.playerName} / ${record.coordinateName}`,
    url: record.code.imageUrl,
  } : {
    key: `friend:${record.key}`,
    name: `${record.playerName} / ${record.coordinateName}`,
    url: record.code.imageUrl,
    star: "",
    brand: "",
    category: null,
    items: [],
  };
}
